import { Box, Stack, Typography } from "@mui/material";
import Pill from "../Pill";
import { onboardingTokens } from "../../theme";
import { t } from "../../../../locales/t";

/**
 * A labelled group of pill buttons over a vocabulary, single- or multi-select.
 *
 * The sub-flows ask this same question shape repeatedly — the stage to learn
 * from, the asker's own stage, connection types, career goals, learning formats
 * — differing only in the list and in whether more than one answer is allowed.
 *
 * `multiple` decides both the value type and the semantics, which is why it is
 * one component and not two: `string[]` and toggle-to-add, or `string` and
 * pick-one where re-clicking clears. Clearing matters — a required single
 * choice with no way to unpick it is a trap when the member mis-taps.
 *
 * Dumb: the caller owns the value and the vocabulary. Options are ids
 * (`vocabulary.member`), resolved to copy here so callers never handle labels.
 *
 * Real `<button>`s with `aria-pressed`, and selected state carries a border and
 * a weight change, never colour alone.
 *
 * @param {object} props
 * @param {string} props.label
 * @param {string} [props.hint]      e.g. "(optional — select all that apply)"
 * @param {string[]} props.options   vocabulary ids
 * @param {string[]|string} props.value
 * @param {(next: string[]|string) => void} props.onChange
 * @param {boolean} [props.multiple]
 * @param {number} [props.max]  cap on selections; multi-select only
 */
const OptionPills = ({
  label,
  hint,
  options,
  value,
  onChange,
  multiple = false,
  max,
}) => {
  const selected = multiple ? (value ?? []) : [];
  // Past the cap the unpicked pills are disabled: still visible, so the list
  // does not reflow under the member's finger mid-selection, and out of the tab
  // order, because there is nothing to do with them. Hiding them would reflow;
  // leaving them active would mean a click that silently does nothing.
  //
  // Only the unpicked ones — a member's own choices stay focusable, so
  // deselecting one is always reachable and re-opens the rest. That is what
  // makes `disabled` the right control here rather than `aria-disabled`:
  // nothing is trapped by dropping them from the tab order.
  const atCap = Boolean(max) && selected.length >= max;
  const isPicked = (option) =>
    multiple ? (value ?? []).includes(option) : value === option;

  const toggle = (option) => {
    if (!multiple) {
      // Re-clicking the current answer clears it rather than doing nothing.
      onChange(value === option ? "" : option);
      return;
    }
    if (selected.includes(option)) {
      onChange(selected.filter((o) => o !== option));
      return;
    }
    if (atCap) return;
    onChange([...selected, option]);
  };

  return (
    <Box component="fieldset" sx={{ border: 0, p: 0, m: 0 }}>
      <Typography
        component="legend"
        sx={{
          fontWeight: 600,
          fontSize: "0.9375rem",
          mb: 0.25,
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 1.5,
          width: "100%",
        }}
      >
        <Box component="span">
          {label}
          {/* Optionality is stated in the label rather than by omission: an
            unlabelled optional question reads as one you skipped. */}
          {hint && (
            <Box
              component="span"
              sx={{
                fontWeight: 400,
                fontSize: "0.8125rem",
                ml: 0.75,
                color: onboardingTokens.mutedForeground,
              }}
            >
              {hint}
            </Box>
          )}
        </Box>

        {/* Announced, not just shown: a member using a screen reader hits the
            cap with no other signal that anything changed. */}
        {Boolean(max) && (
          <Box
            component="span"
            aria-live="polite"
            sx={{
              fontWeight: 400,
              fontSize: "0.75rem",
              whiteSpace: "nowrap",
              color: atCap
                ? onboardingTokens.teal
                : onboardingTokens.mutedForeground,
            }}
          >
            {t("onboarding.subflow.selectedCount", {
              count: selected.length,
              max,
            })}
          </Box>
        )}
      </Typography>

      <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1.5 }}>
        {options.map((option) => {
          const picked = isPicked(option);
          return (
            <Pill
              key={option}
              selected={picked}
              disabled={atCap && !picked}
              onClick={() => toggle(option)}
            >
              {t(`onboarding.vocab.${option}`)}
            </Pill>
          );
        })}
      </Stack>
    </Box>
  );
};

export default OptionPills;
